import { CompensationProfileModel, CrudAccess, EmployeeIdentityResponse, EmployeeModel, EmployeeStatusModel, JobTitleListModel, PayFrequency, TerminationReasonModel, WagePolicyModel, BasisOfPayModel, BankInfoResponse, StateProvinceModel } from "@herbjoy/apex-api-models";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { EventEmitter } from "../../../../common/components/event-emitter";
import { EmploymentStatusEnum } from "../../../../core/enum/employment-status";
import { EmploymentRecordEditContext, EmploymentRecordInputModel } from "./employment-record-edit-context.factory";
import templateUrl from "./employment-record-editor.component.html";

interface IcdOfficeSelect {
    Id: number;
    Text: string;
}

export const EmploymentRecordEditorComponent = {
    templateUrl,
    bindings: {
        /** The employment record being edited. */
        employmentRecord: "<",
        employeeBankInfo: "<",
        /** The identity of the app user. */
        identity: "<",
        /** Permissions regarding CRUD access to employment record. */
        roleAccess: "<",
        /** The edit context providing the list of options and methods for validating the record. */
        context: "<",
        /** Delegate logic to parent component for showing minimum wage requirements. */
        showMinimumWageRequirements: "&onShowMinimumWageRequirements",
        initOnboarding: "&onInitOnboarding",
        revokeOnboarding: "&onRevokeOnboarding",
        showOnboarding: "<"
    },
    controller: class EmploymentRecordEditor {
        //@Input
        employmentRecord: EmploymentRecordInputModel;
        //@Input
        employeeBankInfo: BankInfoResponse;
        //@Input
        identity: EmployeeIdentityResponse;
        //@Input
        roleAccess: CrudAccess;
        //@Input
        context: EmploymentRecordEditContext;
        //@Output
        showMinimumWageRequirements: EventEmitter<{ employmentRecord: EmploymentRecordInputModel, employeeBankInfo: BankInfoResponse }>;
        initOnboarding: EventEmitter<boolean>;
        revokeOnboarding: EventEmitter<boolean>;

        employee: EmployeeModel;
        pristineRecord: EmploymentRecordInputModel;
        employeeID: number;
        icdOffices: IcdOfficeSelect[];
        employmentStatuses: EmployeeStatusModel[];
        terminationReasons: TerminationReasonModel[];
        payFrequencies: PayFrequency[];
        jobTitles: JobTitleListModel[];
        basisOfPay: BasisOfPayModel[];
        compensationProfiles: CompensationProfileModel[];
        wagePolicies: WagePolicyModel[];
        stateProvinces: StateProvinceModel[];
        oneYearAgo: Date;
        isReadOnly: boolean;
        canInitOnboarding: boolean;
        roleID: number;
        stateWorkingIn: StateProvinceModel;
        stateWorkingInAbbr: string;
        // to-do: move to database table later
        roleHierarchy = [12, 8, 6, 11];

        employmentStatusChange: "rehire" | "termination" | null;
        $onInit() {
            this.employee = this.context.employee;
            this.icdOffices = this.context.icdOffices.map(office =>
                this._mapIcdOfficeSelectList(office, this.context.icdOffices.length > 1));
            this.employmentStatuses = this.context.employmentStatuses;
            this.terminationReasons = this.context.terminationReasons;
            this.payFrequencies = this.context.payFrequencies;
            this.jobTitles = this.context.jobTitles;
            this.basisOfPay = this.context.basisOfPay;
            this.compensationProfiles = this.context.compensationProfiles;
            this.stateProvinces = this.context.stateProvinces;
            if (this.employeeBankInfo) {
                this.stateWorkingIn = this.context.stateProvinces.find(x => x.StateProvinceID === this.employeeBankInfo.StateWorkingIn);
                this.stateWorkingInAbbr = this.context.stateProvinces.find(x => x.StateProvinceID === this.employeeBankInfo.StateWorkingIn)?.StateName ?? "";
            } else {
                this.stateWorkingIn = null;
                this.stateWorkingInAbbr = null;
            }
            this.oneYearAgo = moment().add(-1, "year").toDate();
            this.employeeID = this.employee.Id;
            const syncPeriodEnd = moment(this.employmentRecord.TerminationDate == null ? this.employmentRecord.HireDate : this.employmentRecord.TerminationDate).add(3, "weeks");
            const meridianOffice = this.context.icdOffices.find(o => o.Id === this.employmentRecord.IcdOfficeID) as { MeridianOfficeID?: number } | undefined;
            this.isReadOnly = this.identity.Roles.includes("Owner") && this.employee.MeridianEmployeeID != null && meridianOffice?.MeridianOfficeID != null && moment().isBefore(syncPeriodEnd, "day");
            this.canInitOnboarding = this.identity.IsSiteAdmin || this.identity.Roles.includes("Owner");
            var roles = Array.isArray(this.employmentRecord?.RoleList)
                ? this.employmentRecord.RoleList.filter(role => role.IsInRole === true)
                : [];
            const hierarchyMap = new Map(this.roleHierarchy.map((id, idx) => [id, idx]));
            this.roleID = roles.filter(role => hierarchyMap.has(role.RoleID)).sort((a, b) => hierarchyMap.get(a.RoleID) - hierarchyMap.get(b.RoleID))[0]?.RoleID ?? null;
            //this.roleID = Array.isArray(this.employmentRecord?.RoleList)
            //    ? this.employmentRecord.RoleList.find(role => role.IsInRole === true)?.RoleID ?? null : null;
        }

        $onChanges(changesObj) {
            if (changesObj.employmentRecord && this.employmentRecord) {
                this.pristineRecord = cloneDeep(this.employmentRecord);
            }
        }

        onShowOnboardingPopup() {
            return alert("Please reach out to Apex Support: ApexSuppport@herbjoyent.com to enable onboarding for your account.");
        }

        onChangeEmploymentStatus() {
            this.employmentRecord.EmployeeStatusID = this.employmentRecord.EmployeeStatus.EmployeeStatusID;
            if (this.pristineRecord.EmployeeStatusID === EmploymentStatusEnum.Terminated
                && this.employmentRecord.EmployeeStatusID === EmploymentStatusEnum.Active) {
                this.employmentStatusChange = "rehire";
                this.employmentRecord.HireDate = null;
                this.employmentRecord.TerminationDate = null;
                this.employmentRecord.TerminationReasonID = null;
            } else if (this.pristineRecord.EmployeeStatusID !== EmploymentStatusEnum.Terminated
                && this.employmentRecord.EmployeeStatusID === EmploymentStatusEnum.Terminated) {
                this.employmentStatusChange = "termination";
                this.employmentRecord.TerminationDate = null;
                this.employmentRecord.TerminationReasonID = null;
            } else if (this.pristineRecord.EmployeeStatusID === this.employmentRecord.EmployeeStatusID) {
                // Revert to original hire and termination date if changing back to the original status.
                this.employmentStatusChange = null;
                this.employmentRecord.HireDate = this.pristineRecord.HireDate;
                this.employmentRecord.TerminationDate = this.pristineRecord.TerminationDate;
                this.employmentRecord.TerminationReasonID = this.pristineRecord.TerminationReasonID;
            }
        }

        //onInitOnboarding() {

        //}

        //onRevokeOnboarding() {

        //}

        minHireDate(office) {
            if (office.EmployeeID && this.employmentStatusChange !== "rehire") {
                // No limits if the employee already exists.
                return new Date(1900, 0, 1);
            }

            // Hire dates cannot be set more than a year before the current date.
            return this.oneYearAgo;
        }

        changeTerminationReason() {
            this.context.changeTerminationReason(this.employmentRecord);
        }

        changeStateWorkingIn() {
            if (this.employmentRecord.icdOfficeEmployeeForm && this.stateWorkingIn != null && this.stateWorkingIn.StateProvinceID === 0) {
                this.employmentRecord.icdOfficeEmployeeForm.$setValidity("required", false, undefined);
            }
            this.employeeBankInfo.StateWorkingIn = this.stateWorkingIn.StateProvinceID;
            this.stateWorkingInAbbr = this.stateWorkingIn.StateName;
            this.validateMinimumWage();
        }

        changeEmploymentRecordIcdOffice() {
            this.context.changeEmploymentRecordIcdOffice(this.employmentRecord);
            if (this.employmentRecord.icdOfficeEmployeeForm) {
                this.employmentRecord.icdOfficeEmployeeForm.$setValidity("at-least-one-role",
                    this.anyRolesAssigned(), undefined);
            }
            this.validateMinimumWage();
        }

        changePayFrequency() {
            this.context.changePayFrequency(this.employmentRecord);
        }

        changeJobTitle() {
            this.context.changeJobTitle(this.employmentRecord);
        }

        changeBasisOfPay() {
            this.context.changeBasisOfPay(this.employmentRecord);
        }

        changeRole() {
            if (Array.isArray(this.employmentRecord?.RoleList)) {
                this.employmentRecord.RoleList.forEach(role => {
                    role.IsInRole = role.RoleID === this.roleID;
                });
            }
            this.context.changeRole(this.employmentRecord);
            if (this.employmentRecord.icdOfficeEmployeeForm) {
                this.employmentRecord.icdOfficeEmployeeForm.$setValidity("at-least-one-role",
                    this.anyRolesAssigned(), undefined);
            }
        }

        changeCompensationProfile() {
            this.context.changeCompensationProfile(this.employmentRecord);
        }

        validateMinimumWage() {
            this.context.validateMinimumWageV2(this.employmentRecord, this.employeeBankInfo != null ? this.employeeBankInfo.StateWorkingIn : 0);
        }

        anyRolesAssigned() {
            return this.context.anyRolesAssigned(this.employmentRecord);
        }

        _mapIcdOfficeSelectList(office, withOwner) {
            return {
                Id: office.Id,
                Text: withOwner
                    ? `${office.OwnerName} - ${office.IcdCorporationName}, ${office.OfficeName}`
                    : `${office.IcdCorporationName} - ${office.OfficeName}`
            };
        }
    }
}
